import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Form,
    FormField,
    Heading,
    Notification,
    Page,
    PageContent,
    Text,
    TextInput,
} from 'grommet';
import { StatusCritical, StatusGood } from 'grommet-icons';
import {
    useLoaderData,
    useNavigate,
    useParams,
    redirect,
} from 'react-router-dom';
import { API } from '../../api';
import UserService from '../../api/user-service';
import AppConstants from '../../helpers/AppConstants';
import { hasToken, hasUserId } from '../../auth/authentication/Login';
import PropTypes from 'prop-types';

const defaultValues = { password: '', confirmPassword: '' };

export const ChangePassword = ({ slugParam, portalLabel }) => {
    const user = useLoaderData();
    const params = useParams();
    const slug = params[slugParam];
    const navigate = useNavigate();

    const [value, setValue] = useState(defaultValues);
    const [buttonBusy, setButtonBusy] = useState(false);
    const [matchError, setMatchError] = useState('');
    const [successToast, setSuccessToast] = useState(false);
    const [failureToast, setFailureToast] = useState({
        visible: false,
        message: '',
    });
    const redirectTimerRef = useRef(null);

    useEffect(() => () => clearTimeout(redirectTimerRef.current), []);

    const handleSubmit = async () => {
        if (value.password !== value.confirmPassword) {
            setMatchError('Passwords do not match');
            return;
        }
        setMatchError('');
        setButtonBusy(true);
        try {
            await API.put('/api/users/me', {
                email: user.email,
                password: value.password,
                confirmPassword: value.confirmPassword,
            });
            setSuccessToast(true);
            redirectTimerRef.current = setTimeout(
                () => navigate(`/${slug}/dashboard`),
                2000,
            );
        } catch (error) {
            console.log(error);
            setFailureToast({
                visible: true,
                message: error?.message ?? '',
            });
        }
        setButtonBusy(false);
    };

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent align="center">
                <Box width="medium">
                    <Heading level="2" margin={{ bottom: 'none' }}>
                        {portalLabel}
                    </Heading>
                    <Text margin={{ bottom: 'medium' }} color="dark-5">
                        Set a new password before continuing.
                    </Text>
                    <Form
                        value={value}
                        onChange={(nextValue) => setValue(nextValue)}
                        onSubmit={handleSubmit}>
                        <Box gap="medium">
                            <FormField
                                label="New Password"
                                name="password"
                                htmlFor="change-password-input"
                                required>
                                <TextInput
                                    id="change-password-input"
                                    name="password"
                                    type="password"
                                />
                            </FormField>
                            <FormField
                                label="Confirm Password"
                                name="confirmPassword"
                                htmlFor="change-confirm-password-input"
                                error={matchError}
                                required>
                                <TextInput
                                    id="change-confirm-password-input"
                                    name="confirmPassword"
                                    type="password"
                                />
                            </FormField>
                            <Button
                                type="submit"
                                primary
                                label="Change Password"
                                busy={buttonBusy}
                                margin={{ top: 'small' }}
                            />
                        </Box>
                    </Form>
                </Box>
            </PageContent>
            {successToast && (
                <Notification
                    toast={{ autoClose: true, position: 'top' }}
                    status="normal"
                    time={8000}
                    title="Password changed successfully!"
                    icon={<StatusGood color="green" />}
                    onClose={() => setSuccessToast(false)}
                />
            )}
            {failureToast.visible && (
                <Notification
                    toast={{ autoClose: true, position: 'top' }}
                    status="critical"
                    time={8000}
                    title="Failed to change password"
                    message={failureToast.message || undefined}
                    icon={<StatusCritical color="red" />}
                    onClose={() =>
                        setFailureToast({ visible: false, message: '' })
                    }
                />
            )}
        </Page>
    );
};

ChangePassword.propTypes = {
    slugParam: PropTypes.string.isRequired,
    portalLabel: PropTypes.string.isRequired,
};

export async function changePasswordLoader({ params, slugParam, entityKey }) {
    if (!hasToken() || !hasUserId()) {
        return redirect(`/${params[slugParam]}/login`);
    }

    const userId = localStorage.getItem(AppConstants.cacheKeys.userId);
    try {
        const user = await new UserService().getUser(userId);

        if (!user) {
            return redirect(`/${params[slugParam]}/login`);
        }

        const entitySlug = user[entityKey]?.slug;
        if (!entitySlug || entitySlug !== params[slugParam]) {
            return redirect(`/${params[slugParam]}/login`);
        }

        if (user.tempPasswordChanged) {
            return redirect(`/${params[slugParam]}/dashboard`);
        }

        return user;
    } catch {
        return redirect(`/${params[slugParam]}/login`);
    }
}
